import FilterListIcon from '@mui/icons-material/FilterList';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCurrentBaseUser } from 'hooks/user/useCurrentBaseUser';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UISitterDisplay } from 'types/ui.sitter.types';
import { ServiceType } from '../../../happypaws-shared/src/Services/services';
import { useGetSitters } from '../../../hooks/sitter/useGetSitters';
import { useGeolocateUser } from '../../../hooks/user/useGeolocateUser';
import { SearchFiltersType } from '../../../shared/types';
import { reverseGeocode } from '../../../utils/reverseGeocode';
import { mapSitterToDisplay } from '../../../utils/sitterMappers';
import ClientHeader from '../ClientHeader/ClientHeader';
import MapView from './components/MapView';
import SearchFilters from './components/SearchFilters';
import SittersList, { SortOption } from './components/SittersList';

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  useGeolocateUser();

  const { data: currentUser } = useCurrentBaseUser();
  const { data: sittersData, isLoading: isSittersLoading } = useGetSitters();

  const [mapPanTo, setMapPanTo] = useState<{ lat: number; lng: number } | null>(null);
  const [handleShrink, setHandleShrink] = useState<boolean>(false);
  const [selectedSitterId, setSelectedSitterId] = useState<string | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(!isBelowMd);
  const [sortBy, setSortBy] = useState<SortOption>('recommended');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  const [filters, setFilters] = useState<SearchFiltersType>({
    serviceType: ServiceType.PET_SITTING,
    location: '',
    startDate: null,
    startTime: '',
    endDate: null,
    endTime: '',
    selectedPets: [],
    priceRange: [1, 250],
    hasFencedYard: false,
    allowsDogsOnFurniture: false,
  });

  const realSitters: UISitterDisplay[] = useMemo(() => {
    if (!sittersData) return [];
    return sittersData.map(mapSitterToDisplay).filter(Boolean) as UISitterDisplay[];
  }, [sittersData]);

  const isLoading = isSittersLoading;

  const maxPrice = useMemo(() => {
    if (!realSitters.length) return 250;
    return Math.max(...realSitters.map((s) => s.price), 250);
  }, [realSitters]);

  const filteredAndSortedSitters = useMemo(() => {
    let filtered = realSitters.filter((sitter) => {
      if (
        filters.priceRange &&
        (sitter.price < filters.priceRange[0] || sitter.price > filters.priceRange[1])
      ) {
        return false;
      }
      if (filters.hasFencedYard && !sitter.hasFencedYard) return false;
      if (filters.allowsDogsOnFurniture && !sitter.allowsDogsOnFurniture) return false;
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'distance':
          if (!currentUser?.lat || !currentUser?.lng) return 0;
          const distA = Math.sqrt(
            Math.pow(a.coordinates.lat - currentUser.lat, 2) +
              Math.pow(a.coordinates.lng - currentUser.lng, 2)
          );
          const distB = Math.sqrt(
            Math.pow(b.coordinates.lat - currentUser.lat, 2) +
              Math.pow(b.coordinates.lng - currentUser.lng, 2)
          );
          return distA - distB;
        case 'recommended':
        default:
          return 0;
      }
    });

    return sorted;
  }, [realSitters, filters, sortBy, currentUser]);

  const paginatedSitters = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedSitters.slice(startIndex, endIndex);
  }, [filteredAndSortedSitters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  useEffect(() => {
    if (!currentUser?.lat || !currentUser?.lng) return;

    reverseGeocode(currentUser.lat, currentUser.lng).then((placeName) => {
      if (placeName) {
        setFilters((prev) => ({ ...prev, location: placeName }));
      }
    });
  }, [currentUser]);

  const handleFiltersChange = (newFilters: Partial<SearchFiltersType>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      serviceType: ServiceType.PET_SITTING,
      location: '',
      startDate: null,
      startTime: '',
      endDate: null,
      endTime: '',
      selectedPets: [],
      priceRange: [1, maxPrice],
      hasFencedYard: false,
      allowsDogsOnFurniture: false,
    });
    setSortBy('recommended');
  };

  const handleSortChange = (sort: SortOption) => {
    setSortBy(sort);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSitterSelect = (sitter: UISitterDisplay) => {
    setSelectedSitterId(sitter.id);
  };

  const handleMapMarkerClick = (sitterId: string) => {
    setSelectedSitterId(sitterId);
    const sitterIndex = filteredAndSortedSitters.findIndex((s) => s.id === sitterId);
    if (sitterIndex !== -1) {
      const page = Math.floor(sitterIndex / pageSize) + 1;
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    }
  };

  const handleViewProfile = (sitter: UISitterDisplay) => {
    navigate(`/sitter/${sitter.id}`);
  };

  const handleBookNow = (sitter: UISitterDisplay) => {
    navigate(`/sitter/${sitter.id}`);
  };

  const activeFiltersCount =
    (filters.hasFencedYard ? 1 : 0) + (filters.allowsDogsOnFurniture ? 1 : 0);

  const sittersListProps = {
    sitters: paginatedSitters,
    totalSitters: filteredAndSortedSitters.length,
    selectedSitterId,
    isLoading,
    currentPage,
    pageSize,
    sortBy,
    onSitterSelect: handleSitterSelect,
    onViewProfile: handleViewProfile,
    onBookNow: handleBookNow,
    onSortChange: handleSortChange,
    onPageChange: handlePageChange,
    onResetFilters: handleResetFilters,
    setHandleShrink,
    handleShrink,
  };

  const searchFiltersProps = {
    filters,
    maxPrice,
    onClose: () => setIsFiltersOpen(false),
    onFiltersChange: handleFiltersChange,
    onResetFilters: handleResetFilters,
    onLocationSelect: (lat: number, lng: number) => setMapPanTo({ lat, lng }),
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: '#FAF8F5',
        position: 'relative',
      }}
    >
      <ClientHeader />

      {isBelowMd ? (
        // ── Mobile / Tablet layout ──────────────────────────────────────────
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <SittersList {...sittersListProps} />
        </Box>
      ) : (
        // ── Desktop layout (unchanged) ──────────────────────────────────────
        <Box sx={{ position: 'relative', flex: 1 }}>
          {/* Map fills entire area */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}
          >
            <MapView
              sitters={filteredAndSortedSitters}
              selectedSitterId={selectedSitterId}
              onSitterSelect={handleMapMarkerClick}
              userLocation={
                currentUser?.lat && currentUser?.lng
                  ? { lat: currentUser.lat, lng: currentUser.lng }
                  : null
              }
              panTo={mapPanTo}
            />
          </Box>

          {/* Sitters list overlays left */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 2,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            <SittersList {...sittersListProps} />
          </Box>

          {/* Filters panel overlays right */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            <SearchFilters {...searchFiltersProps} isOpen={isFiltersOpen} />
          </Box>
        </Box>
      )}

      {/* ── Filters bottom drawer (mobile / tablet only) ───────────────────── */}
      {isBelowMd && (
        <SwipeableDrawer
          anchor="bottom"
          open={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          onOpen={() => setIsFiltersOpen(true)}
          disableSwipeToOpen={false}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
            },
          }}
        >
          {/* Drag handle */}
          <Box
            sx={{
              width: 40,
              height: 4,
              backgroundColor: '#E0E0E0',
              borderRadius: 2,
              mx: 'auto',
              mt: 1.5,
              mb: 0.5,
              flexShrink: 0,
            }}
          />
          <SearchFilters {...searchFiltersProps} isOpen={true} />
        </SwipeableDrawer>
      )}

      {/* ── FAB ───────────────────────────────────────────────────────────────
           Desktop: appears when the right-side filters panel is closed
           Mobile/tablet: appears when the bottom drawer is closed           */}
      {!isFiltersOpen && (
        <Fab
          color="primary"
          aria-label="open filters"
          onClick={() => setIsFiltersOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 3,
            backgroundColor: '#178B8E',
            '&:hover': { backgroundColor: '#0F6164' },
          }}
        >
          <Badge badgeContent={activeFiltersCount || null} color="error">
            <FilterListIcon />
          </Badge>
        </Fab>
      )}
    </Box>
  );
};

export default ClientDashboard;
